import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { productApi, ProductBulkDelete } from "@/services/productApi";
import { ProductCreate, ProductUpdate } from "@/types/product";
import { toast } from "@/components/ui/use-toast";

export const useProducts = (includeInactive: boolean = true) => {
  return useQuery({
    queryKey: ["products", includeInactive],
    queryFn: () => productApi.getAll(includeInactive),
  });
};

export const useProduct = (id: string) => {
  return useQuery({
    queryKey: ["products", id],
    queryFn: () => productApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateProduct = (options?: {
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProductCreate) => productApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({
        title: "成功",
        description: "產品創建成功",
      });
      options?.onSuccess?.();
    },
    onError: (error: any) => {
      // Error handling is already done in api.ts interceptor
      console.error("Failed to create product:", error);
    },
  });
};

export const useUpdateProduct = (options?: {
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProductUpdate }) => 
      productApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["products", id] });
      toast({
        title: "成功",
        description: "產品更新成功",
      });
      options?.onSuccess?.();
    },
    onError: (error: any) => {
      // Error handling is already done in api.ts interceptor
      console.error("Failed to update product:", error);
    },
  });
};

export const useBulkDeleteProducts = (options?: {
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProductBulkDelete) => productApi.bulkDelete(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      
      if (result.failedCount > 0) {
        toast({
          title: "部分成功",
          description: `成功刪除 ${result.successCount} 個產品，${result.failedCount} 個產品刪除失敗`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "成功",
          description: `成功刪除 ${result.successCount} 個產品`,
        });
      }
      
      options?.onSuccess?.();
    },
    onError: (error: any) => {
      // Error handling is already done in api.ts interceptor
      console.error("Failed to delete products:", error);
    },
  });
};